import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Camera, LinkSimple, Paperclip, Plus, PushPin, Trash, X } from '@phosphor-icons/react';
import { apiLaminas } from '@/api/laminas';
import type { Chinche, Foto, Vinculo } from '@/api/laminas';
import { SearchableSelect } from '@/components/common/SearchableSelect';
import { useCreateTask, useTasks } from '@/hooks/useTasks';

// Lo que va CLAVADO a un punto del plano: los chinches de tarea, los vínculos
// de navegación y las fotos de obra. Son la misma pieza visual — una pastilla
// que no crece con el zoom — así que viven en el mismo archivo y comparten
// `Chincheta`. Antes el visor tenía el bloque de los chinches escrito a mano;
// cada capa nueva habría sido otra copia de esas mismas veinte líneas.

/**
 * Una pastilla anclada a `x, y` de la hoja. La contra-escala es el punto: un
 * chinche tiene que medir igual en pantalla a cualquier zoom, como en
 * Fieldwire, o a la escala de «encajar» es un punto invisible y al acercarse
 * tapa medio plano.
 */
function Chincheta({ x, y, escala, color, titulo, onClick, children }: {
  x: number; y: number; escala: number; color: string;
  titulo: string; onClick?: () => void; children: React.ReactNode;
}) {
  return (
    <div
      title={titulo}
      className="absolute"
      style={{
        left: x, top: y,
        transform: `translate(-50%, -100%) scale(${1 / escala})`,
        transformOrigin: 'bottom center',
        pointerEvents: onClick ? 'auto' : 'none',
      }}
    >
      <div className="flex flex-col items-center">
        <button
          type="button"
          disabled={!onClick}
          // `stopPropagation` o el toque sigue hasta el contenedor y arranca un
          // pan: el plano se movería bajo el dedo mientras se salta de lámina.
          onPointerDown={e => e.stopPropagation()}
          onClick={onClick}
          className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-bold text-white shadow disabled:cursor-default"
          style={{ background: color }}
        >
          {children}
        </button>
        <div className="h-2 w-px" style={{ background: color }} />
      </div>
    </div>
  );
}

// ── Chinches de tarea (F-8: el puente a Tareas) ─────────────────────────────

/**
 * Los chinches pertenecen al PLANO: no se borran al subir una versión nueva.
 *
 * F-8 cambió lo que PINTAN. Antes el chinche decía `#12` —un número muerto, sin
 * clic— y el color salía de `LamChinches.Color`. Ahora el `tareaId` se resuelve
 * contra la lista viva de tareas y el chinche se pinta con el `colorEstado`
 * REAL de la tarea.
 *
 * La diferencia no es cosmética: un color copiado en la fila del chinche sería
 * una caché que miente en cuanto alguien mueva la tarea en el kanban, y el plano
 * es justo donde esa mentira no se nota. Resolviéndolo, mover la tarea repinta
 * el plano solo.
 *
 * Las tareas se leen con el MISMO hook y la MISMA queryKey que el kanban por
 * estado, así que quien ya pasó por el tablero no paga ninguna request de más.
 */
export function CapaChinches({ chinches, escala, onAbrirTarea, onBorrar }: {
  chinches: Chinche[];
  escala: number;
  onAbrirTarea?: (idTarea: number) => void;
  onBorrar?: (idChinche: number) => void;
}) {
  const { data: tareas } = useTasks('status');
  const porId = useMemo(
    () => new Map((tareas || []).map(t => [t.idTareaArq, t])),
    [tareas],
  );

  return (
    <>
      {chinches.map(c => {
        const t = c.tareaId !== null ? porId.get(c.tareaId) : undefined;
        // `Titulo` es la foto del nombre al momento de clavar. Se usa SOLO
        // cuando la tarea no está en la lista; si está, gana el nombre vivo
        // (alguien la pudo renombrar después).
        const nombre = t?.nombreTarea || c.titulo;
        // Gris Hielera cuando no se pudo resolver: el chinche sigue visible
        // —esconderlo deja un hueco que nadie va a arreglar— pero se ve apagado.
        const color = t?.colorEstado || '#888780';
        return (
          <Chincheta
            key={c.id} x={c.posX} y={c.posY} escala={escala} color={color}
            titulo={
              onBorrar ? `Despinchar «${nombre}» del plano (la tarea sigue viva)`
                : t ? `${nombre}\n${t.nomEstado || 'Sin estado'}${t.nomResponsable ? ` · ${t.nomResponsable}` : ''}`
                  // No decimos «borrada»: lo único medible es que no viene en la
                  // lista, y eso también pasa con una tarea inactiva.
                  : `${nombre}\nLa tarea #${c.tareaId} no está en la lista activa`
            }
            onClick={
              onBorrar
                ? () => { if (confirm(`¿Despinchar «${nombre}»?\n\nSale del plano; la tarea NO se borra.`)) onBorrar(c.id); }
                : c.tareaId !== null && onAbrirTarea ? () => onAbrirTarea(c.tareaId!) : undefined
            }
          >
            {onBorrar ? <Trash size={11} weight="bold" /> : <PushPin size={11} weight="bold" />}
            {/* Los puntos suspensivos no son adorno: sin ellos «Revisar el
                detalle de la viga V-3» sale como «Revisar el detal» y se lee
                como un nombre mal escrito en vez de uno cortado. El nombre
                completo está en el tooltip. */}
            {c.tareaId !== null && `#${c.tareaId} `}
            {nombre.length > 16 ? `${nombre.slice(0, 16)}…` : nombre}
          </Chincheta>
        );
      })}
    </>
  );
}

// ── Vínculos entre planos ───────────────────────────────────────────────────

/**
 * La burbuja de detalle de Procore: un punto del plano que lleva a otra lámina
 * (o a un archivo). El nombre del destino se resuelve contra la lista de
 * láminas, que ya está en el caché de TanStack — sin esto el vínculo diría
 * «lámina 7» y habría que saltar para saber adónde va.
 */
export function CapaVinculos({ vinculos, escala, onIr, onBorrar }: {
  vinculos: Vinculo[];
  escala: number;
  onIr?: (idPlano: number) => void;
  onBorrar?: (idVinculo: number) => void;
}) {
  const { data } = useQuery({ queryKey: ['laminas'], queryFn: () => apiLaminas.lista() });
  const nombres = useMemo(
    () => new Map((data || []).map(p => [p.id, p.nombre])),
    [data],
  );

  return (
    <>
      {vinculos.map(v => {
        const destino = v.planoDestinoId !== null ? nombres.get(v.planoDestinoId) : null;
        // Un vínculo que apunta a una lámina borrada no se puede abrir, pero
        // tampoco se esconde: esconderlo deja un hueco que nadie va a arreglar.
        const abrible = v.planoDestinoId !== null && nombres.has(v.planoDestinoId);
        const etiqueta = v.descripcion || destino || v.urlAdjunto?.split('/').pop() || 'Vínculo';
        return (
          <Chincheta
            key={v.id}
            x={v.posX} y={v.posY} escala={escala}
            color={v.color || '#378ADD'}
            titulo={
              // En modo desvincular el tooltip tiene que decir lo que el clic va
              // a HACER, no adónde lleva: un «Ir a A-101» sobre un botón que
              // borra es una trampa.
              onBorrar ? `Quitar el vínculo «${etiqueta}»`
                : v.urlAdjunto ? `Abrir ${v.urlAdjunto}`
                  : abrible ? `Ir a ${destino}`
                    : 'El destino de este vínculo ya no existe'
            }
            onClick={
              onBorrar ? () => { if (confirm(`¿Borrar el vínculo «${etiqueta}»?`)) onBorrar(v.id); }
                : v.urlAdjunto ? () => window.open(v.urlAdjunto!, '_blank', 'noopener')
                  : abrible && onIr ? () => onIr(v.planoDestinoId!)
                    : undefined
            }
          >
            {onBorrar
              ? <Trash size={11} weight="bold" />
              : v.urlAdjunto ? <Paperclip size={11} weight="bold" /> : <LinkSimple size={11} weight="bold" />}
            {etiqueta.slice(0, 18)}
          </Chincheta>
        );
      })}
    </>
  );
}

// ── Fotos de obra ───────────────────────────────────────────────────────────

/** Día legible de una foto: la de la cámara si la hay, si no la de subida. */
function diaDe(f: Foto): string {
  return new Date(f.fechaTomada || f.fechaCreacion)
    .toLocaleDateString('es-CR', { day: '2-digit', month: 'short' });
}

/**
 * Las fotos clavadas al plano. La pastilla muestra el día y NO la imagen: una
 * miniatura por foto obligaría a generarlas en el servidor, y a la escala de
 * «encajar» serían manchas ilegibles tapando el dibujo. La foto se abre de a
 * una, que es como se mira.
 */
export function CapaFotos({ fotos, escala, onVer }: {
  fotos: Foto[];
  escala: number;
  onVer?: (foto: Foto) => void;
}) {
  return (
    <>
      {fotos.map(f => (
        <Chincheta
          key={f.id} x={f.posX} y={f.posY} escala={escala} color="#1D9E75"
          titulo={f.titulo ? `${f.titulo} · ${diaDe(f)}` : `Foto del ${diaDe(f)}`}
          onClick={onVer && (() => onVer(f))}
        >
          <Camera size={11} weight="bold" />
          {f.titulo ? f.titulo.slice(0, 18) : diaDe(f)}
        </Chincheta>
      ))}
    </>
  );
}

/**
 * Clavar un chinche: el diálogo de F-8. Se abre DESPUÉS de tocar el plano, por
 * la misma razón que el del vínculo —el punto es el dato difícil de capturar—.
 *
 * Hay DOS caminos y no tres. Falta a propósito el «chinche suelto» (clavar un
 * título sin tarea) que la tabla 029 permite: un recordatorio pegado al plano y
 * invisible en el tablero del equipo es exactamente la nota que nadie vuelve a
 * leer. Si algo merece un chinche, merece una tarea.
 *
 * El camino de crear pide SOLO el nombre. El backend ya resuelve el resto al
 * crear (estado Hielera, sprint vigente), y el chinche recién clavado abre el
 * TaskModal con un clic: pedir proyecto, categoría y responsable acá sería
 * reescribir ese modal adentro de un popover del visor.
 */
export function DialogoChinche({ onConfirmar, onCancelar }: {
  onConfirmar: (tareaId: number, titulo: string) => void;
  onCancelar: () => void;
}) {
  const { data: tareas } = useTasks('status');
  const crearTarea = useCreateTask();
  const [elegida, setElegida] = useState<number | null>(null);
  const [nombreNuevo, setNombreNuevo] = useState('');
  const [error, setError] = useState<string | null>(null);

  const opciones = useMemo(
    () => (tareas || []).map(t => ({
      value: t.idTareaArq,
      label: `#${t.idTareaArq} ${t.nombreTarea || 'Sin nombre'}`,
      sublabel: [t.nomEstado, t.nomProyecto, t.nomResponsable].filter(Boolean).join(' · ') || 'Sin datos',
    })),
    [tareas],
  );

  const clavarExistente = () => {
    const t = (tareas || []).find(x => x.idTareaArq === elegida);
    if (t) onConfirmar(t.idTareaArq, t.nombreTarea || `Tarea ${t.idTareaArq}`);
  };

  const crearYClavar = async () => {
    const nombre = nombreNuevo.trim();
    if (!nombre) return;
    setError(null);
    try {
      const t = await crearTarea.mutateAsync({ nombreTarea: nombre });
      onConfirmar(t.idTareaArq, t.nombreTarea || nombre);
    } catch (e) {
      // Sin esto el diálogo se queda mudo y el usuario vuelve a apretar: el
      // chinche no se clavó y no hay forma de saber por qué.
      setError(e instanceof Error ? e.message : 'No se pudo crear la tarea.');
    }
  };

  return (
    <div className="pointer-events-auto absolute inset-0 z-40 flex items-center justify-center bg-black/20 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-brand-gray-200 bg-white p-4 shadow-xl">
        <div className="mb-3 flex items-center gap-2">
          <PushPin size={16} weight="bold" className="text-brand-gray-500" />
          <p className="flex-1 text-sm font-semibold text-brand-gray-500">Clavar una tarea acá</p>
          <button type="button" onClick={onCancelar} aria-label="Cancelar"
            className="rounded p-1 text-brand-gray-400 hover:bg-brand-gray-100">
            <X size={14} weight="bold" />
          </button>
        </div>

        <label className="mb-1 block text-xs font-medium text-brand-gray-400">Tarea existente</label>
        <SearchableSelect
          options={opciones}
          value={elegida}
          onChange={v => { setElegida(v); setNombreNuevo(''); }}
          placeholder={opciones.length ? 'Buscá por número o nombre…' : 'No hay tareas para clavar'}
          disabled={opciones.length === 0 || crearTarea.isPending}
        />
        <button
          type="button"
          data-chinche-clavar
          disabled={elegida === null || crearTarea.isPending}
          onClick={clavarExistente}
          className="mt-2 w-full rounded bg-brand-green-100 px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand-green-200 disabled:opacity-40"
        >
          Clavar esta tarea
        </button>

        <div className="my-3 flex items-center gap-2">
          <div className="h-px flex-1 bg-brand-gray-200" />
          <span className="text-[10px] font-bold uppercase tracking-wide text-brand-gray-400">o</span>
          <div className="h-px flex-1 bg-brand-gray-200" />
        </div>

        <label className="mb-1 block text-xs font-medium text-brand-gray-400">Crear una tarea nueva</label>
        <input
          data-chinche-nombre
          value={nombreNuevo}
          onChange={e => { setNombreNuevo(e.target.value); setElegida(null); }}
          onKeyDown={e => { if (e.key === 'Enter') crearYClavar(); }}
          placeholder="Revisar el detalle de la viga…"
          disabled={crearTarea.isPending}
          className="w-full rounded border border-brand-gray-300 px-2 py-1.5 text-sm text-brand-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green-100"
        />
        <button
          type="button"
          data-chinche-crear
          disabled={!nombreNuevo.trim() || crearTarea.isPending}
          onClick={crearYClavar}
          className="mt-2 flex w-full items-center justify-center gap-1 rounded border border-brand-gray-300 px-3 py-1.5 text-xs font-semibold text-brand-gray-500 hover:bg-brand-gray-100 disabled:opacity-40"
        >
          <Plus size={12} weight="bold" />
          {crearTarea.isPending ? 'Creando…' : 'Crear y clavar'}
        </button>
        <p className="mt-1.5 text-[10px] leading-snug text-brand-gray-400">
          Nace en Hielera. Tocá el chinche para abrirla y completar proyecto,
          responsable y fechas.
        </p>

        {error && (
          <p data-chinche-error className="mt-2 rounded bg-brand-red-100/20 px-2 py-1 text-[11px] text-brand-red-200">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}

/**
 * Elegir el destino del vínculo recién pinchado. Se abre DESPUÉS de tocar el
 * plano y no antes: el punto es el dato difícil de capturar, y preguntar el
 * destino primero obligaría a recordarlo mientras se busca dónde pinchar.
 */
export function DialogoVinculo({ idPlanoActual, onConfirmar, onCancelar }: {
  idPlanoActual: number;
  onConfirmar: (planoDestinoId: number, descripcion: string) => void;
  onCancelar: () => void;
}) {
  const { data } = useQuery({ queryKey: ['laminas'], queryFn: () => apiLaminas.lista() });
  const [destino, setDestino] = useState<number | null>(null);
  const [descripcion, setDescripcion] = useState('');

  // La propia lámina se excluye: un vínculo a sí misma no lleva a ningún lado.
  const opciones = (data || [])
    .filter(p => p.id !== idPlanoActual)
    .map(p => ({ value: p.id, label: p.nombre, sublabel: p.carpeta || 'Sin carpeta' }));

  return (
    <div className="pointer-events-auto absolute inset-0 z-40 flex items-center justify-center bg-black/20 p-4">
      <div className="w-full max-w-sm rounded-2xl border border-brand-gray-200 bg-white p-4 shadow-xl">
        <div className="mb-3 flex items-center gap-2">
          <LinkSimple size={16} weight="bold" className="text-brand-gray-500" />
          <p className="flex-1 text-sm font-semibold text-brand-gray-500">Vincular a otra lámina</p>
          <button type="button" onClick={onCancelar} aria-label="Cancelar"
            className="rounded p-1 text-brand-gray-400 hover:bg-brand-gray-100">
            <X size={14} weight="bold" />
          </button>
        </div>

        <label className="mb-1 block text-xs font-medium text-brand-gray-400">Lámina de destino</label>
        <SearchableSelect
          options={opciones}
          value={destino}
          onChange={setDestino}
          placeholder={opciones.length ? 'Elegí la lámina…' : 'No hay otra lámina en el juego'}
          disabled={!opciones.length}
        />

        <label className="mb-1 mt-3 block text-xs font-medium text-brand-gray-400">
          Etiqueta <span className="font-normal">(opcional)</span>
        </label>
        <input
          value={descripcion}
          onChange={e => setDescripcion(e.target.value)}
          placeholder="Detalle 3, Corte A-A…"
          className="w-full rounded border border-brand-gray-300 px-2 py-1.5 text-sm text-brand-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-green-100"
        />

        <div className="mt-4 flex justify-end gap-2">
          <button type="button" onClick={onCancelar}
            className="rounded border border-brand-gray-300 px-3 py-1.5 text-xs font-semibold text-brand-gray-500 hover:bg-brand-gray-100">
            Cancelar
          </button>
          <button
            type="button"
            disabled={destino === null}
            onClick={() => destino !== null && onConfirmar(destino, descripcion.trim())}
            className="rounded bg-brand-green-100 px-3 py-1.5 text-xs font-semibold text-black hover:bg-brand-green-200 disabled:opacity-40"
          >
            Vincular
          </button>
        </div>
      </div>
    </div>
  );
}
